import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import _ from "lodash";
import BackButton from "@/components/Common/BackButton";
import { PageWrapper } from "@/components/PageWrapper";
import PhotoGallery from "@/components/Common/PhotoGallery";
import MapBoxComponent from "@/components/Common/MapboxComponent";
import LakeViewIcon from "../../images/lake-view-icon.svg";
import { getMlsListingByKey } from "@/services/v2/mlsListings";
import { getMatches } from "@/services/v2/matches";
import { formatMatchCountLabel } from "@/helpers/matchCountLabel";
import { MLS_MATCH_MIN_SCORE } from "@/hooks/useWantAdMlsMatches";
import { useCurrentWebsite } from "@/hooks/useCurrentWebsite";
import { pageURLs } from "@/constants";
import {
  thousandSeperatedValue,
  removeDecimalZeros,
} from "@/helpers/common";

const STATUS_LABEL = {
  Active: "Active",
  ActiveUnderContract: "Under Contract",
  Pending: "Pending",
  Closed: "Closed",
  Expired: "Expired",
  Withdrawn: "Withdrawn",
};

const STATUS_VARIANT = {
  Active: "status-pill--active",
  ActiveUnderContract: "status-pill--contract",
  Pending: "status-pill--pending",
};

// React Query key for an MLS listing's want-listing match count.
const MLS_MATCHES_KEY = "mlsListingMatches";

const formatPrice = (p) => {
  if (p === null || p === undefined || p === "") return null;
  const n = parseFloat(p);
  if (Number.isNaN(n) || n <= 0) return null;
  return "$" + thousandSeperatedValue(removeDecimalZeros(n));
};

const formatDate = (d) => {
  if (!d) return "";
  try {
    return new Date(d).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return d;
  }
};

const buildAddressLine1 = (d) => {
  if (d?.UnparsedAddress) return d.UnparsedAddress;
  return [d?.StreetNumber, d?.StreetName, d?.StreetSuffix, d?.UnitNumber]
    .filter(Boolean)
    .join(" ");
};

const buildAddressLine2 = (d) => {
  const cs = [d?.City, d?.StateOrProvince].filter(Boolean).join(", ");
  return [cs, d?.PostalCode].filter(Boolean).join(" ");
};

const MlsListingDetail = () => {
  const navigate = useNavigate();
  const { listingKey } = useParams();
  const { isGitchaDefaultWebsite, mls } = useCurrentWebsite();
  const currentUserId = useSelector(
    (s) => s.userProfile?.userDetails?.[0]?.UserId,
  );

  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorStatus, setErrorStatus] = useState(null);

  // Defense-in-depth: redirect off default Gitcha
  useEffect(() => {
    if (isGitchaDefaultWebsite === true) {
      navigate(pageURLs.home, { replace: true });
    }
  }, [isGitchaDefaultWebsite, navigate]);

  // Fetch listing
  useEffect(() => {
    let cancelled = false;
    if (!listingKey) return;
    setLoading(true);
    setErrorStatus(null);
    getMlsListingByKey(listingKey)
      .then((res) => {
        if (cancelled) return;
        const data = res?.data || null;
        if (!data) {
          setErrorStatus(404);
        } else {
          setListing(data);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        const status = err?.response?.status;
        setErrorStatus(status || 500);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [listingKey]);

  // Scroll restore on mount (from any "See matches" round-trip)
  useEffect(() => {
    if (!listing) return;
    const saved = sessionStorage.getItem("mlsListingDetailScrollY");
    if (saved) {
      requestAnimationFrame(() => {
        window.scrollTo(0, parseInt(saved, 10) || 0);
        sessionStorage.removeItem("mlsListingDetailScrollY");
      });
    }
  }, [listing]);

  const handleSeeMatches = () => {
    if (!listingKey) return;
    sessionStorage.setItem("mlsListingDetailScrollY", String(window.scrollY));
    navigate(`/mls-listing/${encodeURIComponent(listingKey)}/matches`, {
      state: {
        address: buildAddressLine1(listing),
        cityStateZip: buildAddressLine2(listing),
      },
    });
  };

  const agentUserId = _.get(listing, "AgentUserId");
  // Only the agent who owns this listing may view its matches
  const isOwnListing =
    agentUserId != null &&
    currentUserId != null &&
    String(agentUserId) === String(currentUserId);

  // Match count for the "See matches" button - must use the same 50-score
  // threshold as the page this button navigates to (handleSeeMatches ->
  // MlsListingMatches.js), or the button undercounts what the list actually
  // shows. Hooks must run unconditionally on every render, so this lives
  // above the loading/error early returns below (previously placed after
  // them, which threw "Rendered more hooks than during the previous
  // render").
  const { data: matchData } = useQuery({
    queryKey: [MLS_MATCHES_KEY, listingKey],
    enabled: !!listingKey && isOwnListing,
    queryFn: async () => {
      const response = await getMatches({
        mlsListingKey: listingKey,
        minScore: MLS_MATCH_MIN_SCORE,
        page: 1,
        limit: 1,
      });
      return response?.data || {};
    },
  });
  const totalMatches = matchData?.totalCount ?? 0;

  if (loading) {
    return (
      <PageWrapper>
        <div className="mls-detail-page mls-detail-page--loading">
          <BackButton />
          <div className="mls-detail-page__skeleton" />
        </div>
      </PageWrapper>
    );
  }

  if (errorStatus === 404 || !listing) {
    return (
      <PageWrapper>
        <div className="mls-detail-page">
          <BackButton />
          <div className="no-result-wrap">
            <h3>This listing isn't available</h3>
            <p>
              The listing you're looking for may have been removed or isn't part
              of this market.
            </p>
          </div>
        </div>
      </PageWrapper>
    );
  }

  if (errorStatus) {
    return (
      <PageWrapper>
        <div className="mls-detail-page">
          <BackButton />
          <div className="no-result-wrap">
            <h3>Couldn't load this listing</h3>
            <p>Something went wrong. Please try again.</p>
            <button
              className="btn btn-primary"
              onClick={() => {
                setErrorStatus(null);
                setListing(null);
                setLoading(true);
                getMlsListingByKey(listingKey)
                  .then((res) => setListing(res?.data || null))
                  .catch((err) =>
                    setErrorStatus(err?.response?.status || 500),
                  )
                  .finally(() => setLoading(false));
              }}
            >
              Retry
            </button>
          </div>
        </div>
      </PageWrapper>
    );
  }

  const photos = _.get(listing, "media.photos") || [];
  const N2H = _.get(listing, "N2H") || [];
  const status = _.get(listing, "StandardStatus", "");
  const statusLabel = STATUS_LABEL[status] || status;
  const statusVariant = STATUS_VARIANT[status] || "";
  const price = formatPrice(_.get(listing, "ListPrice"));
  const originalPrice = formatPrice(_.get(listing, "OriginalListPrice"));
  const beds = _.get(listing, "BedroomsTotal");
  const baths = _.get(listing, "BathroomsTotalInteger");
  const sqft = _.get(listing, "LivingArea");
  const lotSqft = _.get(listing, "LotSizeSquareFeet");
  const lotAcres = _.get(listing, "LotSizeAcres");
  const yearBuilt = _.get(listing, "YearBuilt");
  const garage = _.get(listing, "GarageSpaces");
  const stories = _.get(listing, "Stories");
  const propertyType =
    _.get(listing, "PropertySubType") || _.get(listing, "PropertyType");
  const accessibilityFeatures = _.get(listing, "AccessibilityFeatures") || [];
  const publicRemarks = _.get(listing, "PublicRemarks", "");
  const subdivision = _.get(listing, "SubdivisionName", "");
  const lat = parseFloat(_.get(listing, "Latitude"));
  const lng = parseFloat(_.get(listing, "Longitude"));
  const hasMap = !Number.isNaN(lat) && !Number.isNaN(lng);

  const agentName = [
    _.get(listing, "MemberFirstName"),
    _.get(listing, "MemberLastName"),
  ]
    .filter(Boolean)
    .join(" ");
  const agentEmail = _.get(listing, "MemberEmail");
  const agentMobile = _.get(listing, "MemberMobilePhone");
  const agentOffice = _.get(listing, "MemberOfficePhone");

  return (
    <PageWrapper>
      <div className="mls-detail-page">
        <BackButton />

        <div className="mls-detail-page__header">
          <div className="mls-detail-page__badge-row">
            <span className="mls-match-card__badge">MLS</span>
            {statusLabel && (
              <span className={`status-pill ${statusVariant}`.trim()}>
                {statusLabel}
              </span>
            )}
            {listing.PublishedOn && (
              <span className="mls-detail-page__meta-date">
                Listed {formatDate(listing.PublishedOn)}
              </span>
            )}
          </div>
        </div>

        {photos.length > 0 && (
          <div className="mls-detail-page__gallery">
            <PhotoGallery photos={photos} isFromDetailsPage={true} />
          </div>
        )}

        <div className="mls-detail-page__body">
          <div className="mls-detail-page__main">
            <div className="mls-detail-page__price-block">
              {price && <h1>{price}</h1>}
              {originalPrice && originalPrice !== price && (
                <span className="mls-detail-page__price-original">
                  Originally {originalPrice}
                </span>
              )}
            </div>

            <div className="mls-detail-page__address">
              <div>{buildAddressLine1(listing)}</div>
              <div>{buildAddressLine2(listing)}</div>
              {subdivision && (
                <div className="mls-detail-page__subdivision">
                  {subdivision}
                </div>
              )}
            </div>

            <div className="mls-detail-page__facts">
              {beds != null && (
                <span>
                  <strong>{beds}</strong> bd
                </span>
              )}
              {baths != null && (
                <span>
                  <strong>{baths}</strong> ba
                </span>
              )}
              {sqft != null && (
                <span>
                  <strong>{thousandSeperatedValue(sqft)}</strong> sqft
                </span>
              )}
              {(lotSqft != null || lotAcres) && (
                <span>
                  <strong>
                    {lotAcres
                      ? `${parseFloat(lotAcres).toFixed(2)} ac`
                      : `${thousandSeperatedValue(lotSqft)} sqft`}
                  </strong>{" "}
                  lot
                </span>
              )}
              {yearBuilt != null && (
                <span>
                  Built <strong>{yearBuilt}</strong>
                </span>
              )}
              {propertyType && (
                <span>
                  <strong>{propertyType}</strong>
                </span>
              )}
              {garage && (
                <span>
                  <strong>{garage}</strong> car
                </span>
              )}
              {stories && (
                <span>
                  <strong>{stories}</strong> stories
                </span>
              )}
            </div>

            {publicRemarks && (
              <section className="mls-detail-page__section">
                <h2>About this listing</h2>
                <p className="mls-detail-page__remarks">{publicRemarks}</p>
              </section>
            )}

            {N2H.length > 0 && (
              <section className="mls-detail-page__section">
                <h2>Features</h2>
                <div className="mls-detail-page__chips">
                  {N2H.map((n, i) => {
                    const text = _.get(n, "Text", "");
                    const icon = _.get(n, "Icon", "");
                    return (
                      <span key={i} className="mls-match-card__chip">
                        <img
                          src={
                            icon
                              ? require("../../images/" + icon)
                              : LakeViewIcon
                          }
                          alt=""
                        />
                        <span>{text}</span>
                      </span>
                    );
                  })}
                </div>
              </section>
            )}

            {accessibilityFeatures.length > 0 && (
              <section className="mls-detail-page__section">
                <h2>Accessibility</h2>
                <ul className="mls-detail-page__list">
                  {accessibilityFeatures.map((f, i) => (
                    <li key={i}>{f}</li>
                  ))}
                </ul>
              </section>
            )}

            {hasMap && (
              <section className="mls-detail-page__section">
                <h2>Location</h2>
                <div className="mls-detail-page__map">
                  <MapBoxComponent center={[lng, lat]} />
                </div>
              </section>
            )}
          </div>

          <aside className="mls-detail-page__sidebar">
            {agentName && (
              <div className="mls-detail-page__agent-card">
                <h3>MLS Agent of Record</h3>
                <div className="mls-detail-page__agent-name">{agentName}</div>
                {agentEmail && (
                  <a
                    className="mls-detail-page__agent-line"
                    href={`mailto:${agentEmail}`}
                  >
                    {agentEmail}
                  </a>
                )}
                {agentMobile && (
                  <a
                    className="mls-detail-page__agent-line"
                    href={`tel:${agentMobile}`}
                  >
                    {agentMobile} (mobile)
                  </a>
                )}
                {agentOffice && (
                  <a
                    className="mls-detail-page__agent-line"
                    href={`tel:${agentOffice}`}
                  >
                    {agentOffice} (office)
                  </a>
                )}
                {agentUserId == null && (
                  <p className="mls-detail-page__agent-note">
                    This agent is not currently active in the{" "}
                    {(mls || "").toUpperCase()} system.
                  </p>
                )}
              </div>
            )}

            <div className="mls-detail-page__meta-card">
              {listing.ListingId && (
                <div>
                  <strong>MLS #:</strong> {listing.ListingId}
                </div>
              )}
              {listing.ModificationTimestamp && (
                <div>
                  <strong>Updated:</strong>{" "}
                  {formatDate(listing.ModificationTimestamp)}
                </div>
              )}
            </div>

            {isOwnListing && (
              <button
                className="btn btn-primary mls-detail-page__cta"
                onClick={handleSeeMatches}
              >
                {formatMatchCountLabel(totalMatches)}
              </button>
            )}
          </aside>
        </div>
      </div>
    </PageWrapper>
  );
};

export default MlsListingDetail;
